import React, { useState, useMemo, useRef } from "react";
import { Switch, Typography, Box, Button, TextField } from "@material-ui/core";
import dayjs from "dayjs";
import { useDebounceFn } from "ahooks";
import { useTranslation } from "@/langs/useTranslation";
import { ReactRender } from "@/components/ReactRender";
import DataTable from "@/components/DataTable";
import AddAppModal from "../AppConfig/AddAppModal";
import { newPasswordShouldBeDifferent } from "@/config/i18nText";
import { getProfileConfigHistoryDeviceList } from "@/apis/apiCollection";

const PackageIdEditText = ({
  value,
  onChange,
  readOnly,
  rowNumber,
  lastFoused,
  lastFousedType,
  type
}) => {
  const { run: handleChange } = useDebounceFn(
    (value, type, index) => {
      onChange({
        target: {
          name: `${peripheralSettingName}.peripherals[${index}].${type}`,
          value
        }
      });
    },
    { wait: 500 }
  );

  if (readOnly) {
    return <>{value}</>;
  }

  return (
    <TextField
      size="small"
      variant="outlined"
      defaultValue={value}
      autoFocus={rowNumber === lastFoused.current && lastFousedType.current === type}
      onFocus={() => {
        lastFoused.current = rowNumber;
        lastFousedType.current = type;
      }}
      onChange={e => {
        handleChange(e.target.value, type, rowNumber);
      }}
      style={{ width: 300 }}
    />
  );
};

const PeripheralManagement = ({ values, onChange, readOnly }) => {
  const { t } = useTranslation();
  const [addAppOpen, setAddAppOpen] = useState(false);

  const valuesRef = useRef();
  valuesRef.current = values;

  const lastFoused = useRef(null);
  const lastFousedType = useRef(null);

  const columns = [
    {
      data: "vid",
      title: "VID",
      orderable: false,
      createdCell: (td, data, row, rowNumber) => {
        ReactRender(
          <>
            {!readOnly ? (
              <PackageIdEditText
                lastFoused={lastFoused}
                lastFousedType={lastFousedType}
                value={row.vid}
                onChange={onChange}
                readOnly={readOnly}
                rowNumber={rowNumber}
                type="vid"
              />
            ) : (
              data
            )}
          </>,
          td
        );
      }
    },
    {
      data: "pid",
      title: "PID",
      orderable: false,
      createdCell: (td, data, row, rowNumber) => {
        ReactRender(
          <>
            {!readOnly ? (
              <PackageIdEditText
                lastFoused={lastFoused}
                lastFousedType={lastFousedType}
                value={row.pid}
                onChange={onChange}
                readOnly={readOnly}
                rowNumber={rowNumber}
                type="pid"
              />
            ) : (
              data
            )}
          </>,
          td
        );
      }
    },
    {
      data: null,
      title: t({ defaultMessage: "操作" }),
      className: "center nowrap",
      searchable: false,
      orderable: false,
      width: 80,
      createdCell: (td, data, row, rowNumber) => {
        ReactRender(
          <Button
            size="small"
            color="primary"
            disabled={readOnly}
            onClick={() => {
              lastFoused.current = null;
              onChange({
                target: {
                  name: `${peripheralSettingName}.peripherals`,
                  value: valuesRef.current.peripherals.filter(
                    (app, i) => i !== rowNumber
                  )
                }
              });
            }}
          >
            {t({ defaultMessage: "移除" })}
          </Button>,
          td
        );
      }
    }
  ];

  const handleAddByPackageId = () => {
    lastFoused.current = null;
    onChange({
      target: {
        name: `${peripheralSettingName}.peripherals`,
        value: [
          {
            name: "",
            vid: "",
            pid: "",
          },
          ...values.peripherals
        ]
      }
    });
  };

  return (
    <Box flex={1}>
      <Typography variant="h5" style={{ marginBottom: 32 }}>
        {t({ defaultMessage: "周边管理" })}
      </Typography>
      <Box display="flex" alignItems="center" flex={1}>
        <Typography variant="h6" style={{ marginRight: "auto" }}>
          {t({ defaultMessage: "是否启用" })}
        </Typography>
        <Switch
          name={`${peripheralSettingName}.open`}
          checked={values.open}
          color="primary"
          onChange={onChange}
          size="small"
        />
      </Box>
      {values.open && (
        <>
          <Box marginTop={3} marginBottom={3}>
            <Button
              variant="outlined"
              color="primary"
              style={{ minWidth: 118, marginLeft: 16 }}
              onClick={handleAddByPackageId}
              disabled={readOnly}
            >
              {t({ defaultMessage: "添加周边设备" })}
            </Button>
          </Box>
          <DataTable
            options={{ serverSide: false }}
            data={values.peripherals}
            columns={columns}
            paging={false}
            showCheckAll={false}
            showFooter={false}
            autoLoad
          />
        </>
      )}
    </Box>
  );
};

export const initValues = {
  open: false,
  peripherals: []
};

export const peripheralSettingName = "peripheralSetting";
export default PeripheralManagement;
